import React from 'react';
import { useTranslation } from 'react-i18next';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StoryCard = ({ story, onViewDetails }) => {
  const { t } = useTranslation();
  
  return (
    <div className="bg-card rounded-lg overflow-hidden shadow-md hover:shadow-elevated transition-all duration-300 group">
      <div className="relative h-48 md:h-56 lg:h-64 overflow-hidden">
        <Image
          src={story?.image}
          alt={story?.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
            {story?.category}
          </span>
          {story?.featured && (
            <span className="px-3 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full flex items-center gap-1">
              <Icon name="Star" size={12} />
              {t('successStories.card.featured')}
            </span>
          )}
        </div>
        {story?.videoAvailable && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/20 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
              <Icon name="Play" size={32} color="var(--color-primary-foreground)" />
            </div>
          </div>
        )}
      </div>
      <div className="p-4 md:p-5 lg:p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary/20">
            <Image
              src={story?.playerAvatar}
              alt={story?.playerAvatarAlt}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="text-base md:text-lg font-bold text-foreground font-headline">
              {story?.playerName}
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground">
              {story?.position}
            </p>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground mb-2">
            <Icon name="MapPin" size={14} />
            <span>{story?.fromClub}</span>
            <Icon name="ArrowRight" size={14} />
            <span className="text-primary font-medium">{story?.toClub}</span>
          </div>
          <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
            <Icon name="Calendar" size={14} />
            <span>{story?.transferDate}</span>
          </div>
        </div>

        <p className="text-sm md:text-base text-foreground mb-4 line-clamp-3">
          {story?.summary}
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {story?.achievements?.slice(0, 3)?.map((achievement, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-success/10 text-success text-xs rounded-md flex items-center gap-1"
            >
              <Icon name="CheckCircle2" size={12} />
              {achievement}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-lg md:text-xl font-bold text-primary font-headline">
                {story?.transferValue}
              </p>
              <p className="text-xs text-muted-foreground">{t('successStories.card.transferValue')}</p>
            </div>
            <div className="text-center">
              <p className="text-lg md:text-xl font-bold text-secondary font-headline">
                {story?.careerGrowth}
              </p>
              <p className="text-xs text-muted-foreground">{t('successStories.card.careerGrowth')}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={() => onViewDetails(story)}
          >
            {t('successStories.card.readMore')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StoryCard;